import React, { useEffect, useState } from 'react';

const API = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function OmniCustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [search, setSearch] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');
  const loadCustomers = () => {
    setLoading(true);
    fetch(`${API}/api/omni-customers?omni365_only=true`)
      .then(r => r.json())
      .then(d => { setCustomers(d.customers || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { loadCustomers(); }, []);

  const save = async (c) => {
    setSaving(s => ({ ...s, [c.project_key]: true }));
    try {
      await fetch(`${API}/api/omni-customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_key: c.project_key, is_omni: c.is_omni, user_count: c.user_count, customer_name: c.customer_name }),
      });
    } finally {
      setSaving(s => ({ ...s, [c.project_key]: false }));
    }
  };

  const update = (key, patch) => setCustomers(prev => prev.map(c => c.project_key === key ? { ...c, ...patch } : c));

  const syncNames = async () => {
    setSyncing(true); setSyncMsg('');
    try {
      const r = await fetch(`${API}/api/omni-customers/sync-names`, { method: 'POST' });
      const d = await r.json();
      if (d.ok) {
        setSyncMsg(`Filled ${d.updated} names`);
        loadCustomers();
        setTimeout(() => setSyncMsg(''), 3000);
      }
    } catch {
      setSyncMsg('Error');
      setTimeout(() => setSyncMsg(''), 3000);
    } finally {
      setSyncing(false);
    }
  };

  const filtered = customers
    .filter(c =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.project_key.toLowerCase().includes(search.toLowerCase()) ||
      (c.customer_name || '').toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const omniCount = customers.filter(c => c.is_omni).length;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          className="input"
          placeholder="Search customer…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: 220 }}
        />
        <span style={{ fontSize: 13, color: 'var(--text-2)' }}>
          {omniCount} Omni365 · {customers.length} customers
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={syncNames} disabled={syncing}
            style={{ padding: '5px 12px', fontSize: 12, border: '1px solid var(--border)',
              borderRadius: 6, cursor: 'pointer', background: 'var(--surface-2)', color: 'var(--text-2)',
              display: 'flex', alignItems: 'center', gap: 6, opacity: syncing ? .6 : 1 }}>
            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"
              style={syncing ? { animation: 'spin 1s linear infinite' } : {}}>
              <polyline points="23 4 23 10 17 10"/>
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
            </svg>
            {syncing ? 'Syncing…' : syncMsg || 'Sync names from Jira'}
          </button>
        </div>
      </div>

      <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 20 }}>
        Each Omni365 user includes 30 min of support per month. Included hours and support ratio appear in Customer Activity.
      </p>

      {loading && <div className="loading">Loading…</div>}

      {!loading && (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Full name</th>
                  <th style={{ textAlign: 'center' }}>Omni365</th>
                  <th style={{ textAlign: 'right' }}>Users</th>
                  <th style={{ textAlign: 'right' }}>Included / month</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => {
                  const includedMin = c.is_omni ? c.user_count * 30 : 0;
                  const h = Math.floor(includedMin / 60), m = includedMin % 60;
                  const includedStr = includedMin > 0 ? (m > 0 ? `${h}h ${m}m` : `${h}h`) : '—';
                  return (
                    <tr key={c.project_key}>
                      <td><span className="badge badge-blue">{c.project_key}</span></td>
                      <td>
                        <input
                          className="input"
                          style={{ width: '100%', minWidth: 160, fontSize: 13 }}
                          value={c.customer_name || ''}
                          placeholder={c.project_key}
                          onChange={e => update(c.project_key, { customer_name: e.target.value })}
                          onBlur={() => save(c)}
                        />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={c.is_omni}
                          style={{ width: 16, height: 16, cursor: 'pointer' }}
                          onChange={async e => {
                            const updated = { ...c, is_omni: e.target.checked };
                            update(c.project_key, { is_omni: e.target.checked });
                            await save(updated);
                          }}
                        />
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {c.is_omni ? (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <input
                                type="number"
                                min={0}
                                value={c.user_count}
                                className="input"
                                style={{ width: 70, textAlign: 'right', fontFamily: 'DM Mono, monospace', fontSize: 13 }}
                                onChange={e => update(c.project_key, { user_count: parseInt(e.target.value) || 0 })}
                                onBlur={() => save(c)}
                              />
                              {saving[c.project_key] && <span style={{ fontSize: 10, color: 'var(--ok)' }}>✓</span>}
                            </div>
                            {c.bc_note && (
                              <div style={{ fontSize: 10, color: 'var(--text-3)', fontFamily: 'DM Mono', textAlign: 'right', maxWidth: 220 }}>
                                {c.bc_note}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: 'var(--text-3)', fontSize: 12 }}>—</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', fontSize: 12, color: includedMin > 0 ? 'var(--text-2)' : 'var(--text-3)' }}>
                        {includedStr}
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-3)', padding: '24px 0' }}>No customers found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
